import { Alert, Input, Space, Table, Tag, Typography } from 'antd'
import {
  ColumnsType,
  ExpandableConfig,
  FilterValue,
  SorterResult,
  TablePaginationConfig,
} from 'antd/es/table/interface'
import React, { FunctionComponent, useCallback, useMemo } from 'react'

import {
  LeadState,
  leadStateColors,
  leadStateLabels,
} from '../../../common/businessContactTypes'
import { useAppSelector } from '../../../redux/hooks'
import {
  BusinessContactTableItem,
  useBusinessContactData,
} from '../../utils/useBusinessContactData'
import { useTableLocale } from '../../utils/useTableLocale'
import { BusinessContactStateEdit } from '../BusinessContactStateEdit/BusinessContactStateEdit'
import { BusinessContactTableAction } from '../BusinessContactTableAction/BusinessContactTableAction'
import { BusinessContactTableExpanded } from '../BusinessContactTableExpanded/BusinessContactTableExpanded'
import { ExpandColumnTitle } from '../ExpandColumnTitle/ExpandColumnTitle'
import { ExpandIcon } from '../ExpandIcon/ExpandIcon'

const tableId = 'business-contact-table'
const scrollX = { x: 1600 }

const stateOptions = Object.values(LeadState).map(value => ({
  text: leadStateLabels[value],
  value,
}))

const normalizeLeadState = (value: unknown): LeadState | undefined => {
  if (typeof value !== 'string') return undefined
  const lowered = value.toLowerCase()
  return (Object.values(LeadState) as string[]).includes(lowered)
    ? (lowered as LeadState)
    : undefined
}

const toTelHref = (phone: string): string => {
  const normalized = phone.replace(/[^\d+]/g, '')
  return `tel:${normalized}`
}

const formatUrlForDisplay = (value: string): string => {
  return value
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/+$/g, '')
}

export const BusinessContactTable: FunctionComponent = () => {
  const { data, loading, error, pagination, params, setParams, refetch } =
    useBusinessContactData()
  const expandedRowKeys = useAppSelector(
    state => state.expandedRow.tables?.[tableId] || []
  )

  const rowClassName = useCallback(
    (record: BusinessContactTableItem) => {
      const isExpanded = expandedRowKeys.includes(record.id)
        ? 'rowExpanded fade-in'
        : ''
      return isExpanded
    },
    [expandedRowKeys]
  )

  const handleTableChange = useCallback(
    (
      paginationConfig: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
      sorter:
        | SorterResult<BusinessContactTableItem>
        | SorterResult<BusinessContactTableItem>[]
    ) => {
      const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter

      // Build orderBy string
      let orderBy: string | undefined
      if (singleSorter?.field && singleSorter?.order) {
        const direction = singleSorter.order === 'ascend' ? 'ASC' : 'DESC'
        orderBy = `${String(singleSorter.field)}_${direction}`
      }

      // Extract state filter
      const stateFilter = filters.state?.[0] as LeadState | undefined

      setParams({
        page: paginationConfig.current || 1,
        pageSize: paginationConfig.pageSize || 50,
        orderBy,
        state: stateFilter,
      })
    },
    [setParams]
  )

  const handleSearch = useCallback(
    (value: string) => {
      setParams({ search: value || undefined })
    },
    [setParams]
  )

  const columns = useMemo<ColumnsType<BusinessContactTableItem>>(
    () => [
      {
        title: 'Název',
        dataIndex: 'title',
        ellipsis: true,
        width: 260,
        sorter: true,
        render: (title: string | null) => title || '---',
      },
      {
        title: 'URL',
        dataIndex: 'websites',
        ellipsis: true,
        width: 150,
        render: (websites: string[] | null) => {
          const primary = websites?.[0]
          return primary ? (
            <a href={primary} target="_blank" rel="noopener noreferrer">
              <Typography.Text
                ellipsis={{ tooltip: primary }}
                className="text-primary hover:text-primaryHover"
              >
                {formatUrlForDisplay(primary)}
              </Typography.Text>
            </a>
          ) : (
            '---'
          )
        },
      },
      {
        title: 'E-maily',
        dataIndex: 'emails',
        ellipsis: true,
        width: 200,
        render: (emails: string[] | null) =>
          emails?.length ? (
            <Space size={6} wrap>
              {emails.map((email, idx) => (
                <a key={`${email}-${idx}`} href={`mailto:${email}`}>
                  {email}
                </a>
              ))}
            </Space>
          ) : (
            '---'
          ),
      },
      {
        title: 'Telefony',
        dataIndex: 'phoneNumbers',
        width: 140,
        render: (phones: string[] | null) =>
          phones?.length ? (
            <Space size={6} wrap>
              {phones.map((phone, idx) => (
                <a key={`${phone}-${idx}`} href={toTelHref(phone)}>
                  {phone}
                </a>
              ))}
            </Space>
          ) : (
            '---'
          ),
      },
      {
        title: 'Město',
        dataIndex: 'locality',
        ellipsis: true,
        width: 150,
        sorter: true,
        render: (locality: string | null) => locality || '---',
      },
      {
        title: 'PSČ',
        dataIndex: 'postalCode',
        width: 90,
        render: (postalCode: string | null) => postalCode || '---',
      },
      {
        title: 'Stav',
        dataIndex: 'state',
        width: 130,
        filters: stateOptions,
        filteredValue: params.state ? [params.state] : null,
        sorter: true,
        render: (state: unknown, record) => {
          const normalized = normalizeLeadState(state)
          const tag = normalized ? (
            <Tag className="cursor-pointer" color={leadStateColors[normalized]}>
              {leadStateLabels[normalized]}
            </Tag>
          ) : (
            <Tag className="cursor-pointer" color="default">
              {typeof state === 'string' ? state : '---'}
            </Tag>
          )

          return (
            <BusinessContactStateEdit record={record} onUpdate={refetch}>
              {tag}
            </BusinessContactStateEdit>
          )
        },
      },
      {
        title: 'Kategorie',
        dataIndex: 'categories',
        ellipsis: true,
        width: 220,
        render: (categories: string[] | null) =>
          categories?.length ? categories.join(', ') : '---',
      },
      {
        title: 'Poznámka',
        dataIndex: 'note',
        render: (_, record) => (
          <BusinessContactTableAction record={record} onUpdate={refetch} />
        ),
      },
    ],
    [params.state, refetch]
  )

  const expandable = useMemo<ExpandableConfig<BusinessContactTableItem>>(() => {
    const allKeys = (data || []).map(record => record.id)

    return {
      expandedRowKeys,
      expandedRowRender: record => (
        <BusinessContactTableExpanded record={record} />
      ),
      columnTitle: <ExpandColumnTitle allKeys={allKeys} tableId={tableId} />,
      expandIcon: ({ expanded, record }) => (
        <ExpandIcon
          expanded={expanded}
          recordId={record.id}
          tableId={tableId}
          expandable
        />
      ),
      rowExpandable: () => true,
    }
  }, [data, expandedRowKeys])

  const tableLocale = useTableLocale(loading)

  const tablePagination = useMemo<TablePaginationConfig>(
    () => ({
      current: pagination.page,
      pageSize: pagination.pageSize,
      total: pagination.total,
      showSizeChanger: true,
      showTotal: (total, range) => `${range[0]}-${range[1]} z ${total} záznamů`,
      pageSizeOptions: ['20', '50', '100', '200'],
    }),
    [pagination]
  )

  if (error) {
    return (
      <Alert
        message="Chyba při načítání dat z Business Contact databáze"
        type="error"
      />
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Input.Search
          placeholder="Hledat v názvu, URL, městě, ulici..."
          allowClear
          onSearch={handleSearch}
          className="max-w-md"
          defaultValue={params.search}
        />
        <span className="text-gray-500">
          Celkem: {pagination.total} záznamů
        </span>
      </div>
      <Table
        id={tableId}
        rowKey="id"
        rowClassName={rowClassName}
        size="small"
        scroll={scrollX}
        dataSource={data}
        columns={columns}
        loading={loading}
        locale={tableLocale}
        pagination={tablePagination}
        expandable={expandable}
        onChange={handleTableChange}
      />
    </div>
  )
}
